import { styled } from '../../../utils/styled'
import Grid from '../../Grid'

const LAYOUT_TRANSITION_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)'
export const MESSAGE_LAYOUT_TRANSITION_MS = 320

interface MessageLayoutContainerProps {
  $isCenteredEmpty: boolean
  $isTransitioning: boolean
}

export const StyledGridMessageLayoutContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== '$isCenteredEmpty' && prop !== '$isTransitioning'
})<MessageLayoutContainerProps>(({ theme, $isCenteredEmpty, $isTransitioning }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  justifyContent: $isCenteredEmpty ? 'center' : 'space-between',
  gap: $isCenteredEmpty ? theme.spacing(3) : 0,
  overflow: $isTransitioning ? 'hidden' : undefined
}))

export const StyledGridMessageLayoutMaxWidth = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'maxWidth' && prop !== '$paddingTop'
})<{ maxWidth: string; $paddingTop?: number }>(({ theme, maxWidth, $paddingTop = 0 }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%',
  maxWidth,
  marginLeft: 'auto',
  marginRight: 'auto',
  ...($paddingTop > 0 ? { paddingTop: `${$paddingTop}px` } : {})
}))

export const StyledGridMessageLayoutMessageListContainer = styled(Grid, {
  shouldForwardProp: (prop) => prop !== '$animateEnter' && prop !== '$offsetTop'
})<{ $animateEnter?: boolean; $offsetTop?: number }>(({ $animateEnter, $offsetTop = 0 }) => ({
  flex: 1,
  minHeight: 0,
  // Pair with overflowX — overflow-y:auto alone computes overflow-x to auto.
  overflowX: 'hidden',
  overflowY: 'auto',
  width: '100%',
  ...($offsetTop > 0 ? { scrollPaddingTop: `${$offsetTop}px` } : {}),
  opacity: $animateEnter === true ? 0 : 1,
  transform: $animateEnter === true ? 'translateY(min(40vh, 280px))' : 'translateY(0)',
  transition: `opacity ${MESSAGE_LAYOUT_TRANSITION_MS}ms ${LAYOUT_TRANSITION_EASING}, transform ${MESSAGE_LAYOUT_TRANSITION_MS}ms ${LAYOUT_TRANSITION_EASING}`
}))

export const StyledGridMessageLayoutEmptyContent = styled(Grid, {
  shouldForwardProp: (prop) => prop !== '$isVisible'
})<{ $isVisible: boolean }>(({ $isVisible }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flexShrink: 0,
  opacity: $isVisible ? 1 : 0,
  pointerEvents: $isVisible ? 'auto' : 'none',
  transition: `opacity ${MESSAGE_LAYOUT_TRANSITION_MS}ms ${LAYOUT_TRANSITION_EASING}`,
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none'
  }
}))

interface ComposerControlsContainerProps {
  isScrolled: boolean
  $isCenteredEmpty: boolean
  $maxWidth: string
  $hasTopAccessory: boolean
}

export const StyledGridMessageLayoutComposerControlsContainer = styled(Grid, {
  shouldForwardProp: (prop) =>
    prop !== 'isScrolled' && prop !== '$isCenteredEmpty' && prop !== '$maxWidth' && prop !== '$hasTopAccessory'
})<ComposerControlsContainerProps>(({ theme, isScrolled, $isCenteredEmpty, $maxWidth, $hasTopAccessory }) => ({
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: $isCenteredEmpty ? 0 : `${theme.spacing(3)} 0 ${theme.spacing(1)} 0`,
  position: 'relative',
  backgroundColor: theme.tokens.background.main.default,
  '&::before': $isCenteredEmpty
    ? undefined
    : {
        content: '""',
        position: 'absolute',
        top: `-${theme.spacing(5)}`,
        left: 0,
        right: 0,
        height: theme.spacing(5),
        background: `linear-gradient(to top, ${theme.tokens.background.main.default}, transparent)`,
        pointerEvents: 'none',
        opacity: isScrolled ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out'
      },
  // Divider sits at the top of the dock when there is no accessory; with a top accessory the
  // accessory owns the divider (border-bottom) so prompts sit above the line.
  '&::after':
    $isCenteredEmpty || $hasTopAccessory
      ? undefined
      : {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: $maxWidth,
          height: '1px',
          backgroundColor: theme.tokens.border.main.tonal,
          pointerEvents: 'none'
        }
}))

export const StyledGridMessageLayoutComposerTopAccessory = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.tokens.border.main.tonal}`
}))

export const MESSAGE_LAYOUT_TRANSITION_EASING = LAYOUT_TRANSITION_EASING
